import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar
} from '@mui/material';
import ImageUpload from './ui/ImageUpload';
import { useImageUpload } from '../hooks/useImageUpload';
import { useAuth } from '../contexts/AuthContext';

interface ProfilePictureUploadProps {
  onUploadSuccess?: () => void;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  onUploadSuccess
}) => {
  const { user, updateUser } = useAuth();
  const [images, setImages] = useState<(string | null)[]>([user?.profile_picture || null]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { loading, error, uploadProfilePicture, clearError } = useImageUpload({
    onSuccess: () => {
      setSuccessMessage('Profile picture updated successfully!');
      onUploadSuccess?.();
    }
  });

  // Update images when user data changes
  useEffect(() => {
    setImages([user?.profile_picture || null]);
  }, [user?.profile_picture]);

  const handleImagesChange = async (newImages: (string | null)[]) => {
    setImages(newImages);
    
    try {
      const result = await uploadProfilePicture(newImages[0]);
      
      // Update user context with new profile picture
      if (user) {
        updateUser({
          ...user,
          profile_picture: result.profile_picture
        });
      }
    } catch (err) {
      // Error is handled by the hook
      console.error('Failed to upload profile picture:', err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Profile Picture
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a profile picture to personalize your account. Maximum file size: 2MB.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <ImageUpload
        images={images}
        maxImages={1}
        onImagesChange={handleImagesChange}
        loading={loading}
        variant="profile"
        size={120}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePictureUpload;
